/**
 * Perfil (docs/spec/settings.md): o avatar, a capa e os três textos públicos, com a prévia de como os
 * outros veem. O avatar e a capa gravam na hora; os textos só vão para o servidor ao salvar: a barra
 * de alterações aparece quando há o que salvar ou descartar.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useT } from '../i18n/index.js';
import { Avatar } from '../social/Avatar.js';
import { useAvatarPicker } from '../social/avatarPicker.js';
import { ProfileCoverPicker, useRemoveCover } from '../social/ProfileCoverPicker.js';
import { profileCoverSrc } from '../social/profileModel.js';
import { useApp } from '../store.js';
import { Button } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';

type Draft = { bio: string; rpg: string; times: string };
const FIELDS: readonly { key: keyof Draft; label: string; placeholder: string; max: number; rows: number }[] = [
  { key: 'bio', label: 'profile.bio', placeholder: 'prefs.profile.bio.placeholder', max: 1000, rows: 4 },
  { key: 'rpg', label: 'profile.rpg', placeholder: 'prefs.profile.rpg.placeholder', max: 500, rows: 2 },
  { key: 'times', label: 'profile.times', placeholder: 'prefs.profile.times.placeholder', max: 200, rows: 2 },
];

function AvatarRow() {
  const t = useT();
  const me = useApp((s) => s.user);
  const setAvatar = useApp((s) => s.setAvatar);
  const picker = useAvatarPicker();
  if (!me) return null;
  return (
    <div className="rd-prefs__avatar" data-pref-row="avatar">
      <Avatar name={me.displayName} color={me.color} size={72} userId={me.id} avatarVersion={me.avatarVersion} />
      <div>
        <div className="rd-prefs__buttons">
          <Button variant="secondary" icon="image" busy={picker.busy} onClick={picker.open}>
            {t('config.profile.avatarPick')}
          </Button>
          {me.avatarVersion > 0 ? (
            <Button variant="link" className="rd-prefs__link" disabled={picker.busy} onClick={() => void setAvatar(null)}>
              {t('config.profile.avatarRemove')}
            </Button>
          ) : null}
        </div>
        <p className="rd-prefs__desc">{t('config.profile.avatarHint')}</p>
        {picker.dialog}
      </div>
    </div>
  );
}

/** A capa do perfil: a prévia (a imagem, ou a faixa na cor da pessoa) e os botões, que gravam na hora, como os do avatar. */
function CoverRow({ cover }: { cover: string | null }) {
  const t = useT();
  const me = useApp((s) => s.user);
  const removeCover = useRemoveCover();
  const [picking, setPicking] = useState(false);
  const [broken, setBroken] = useState<string | null>(null);
  if (!me) return null;
  const src = profileCoverSrc(me.id, cover);
  return (
    <div className="rd-prefs__cover" data-pref-row="cover">
      <span className="rd-prefs__coverPreview" style={{ '--profile-color': me.color } as CSSProperties} aria-hidden="true">
        {src && broken !== src ? <img src={src} alt="" onError={() => setBroken(src)} /> : null}
      </span>
      <div>
        <div className="rd-prefs__buttons">
          <Button variant="secondary" icon="image" onClick={() => setPicking(true)}>
            {t('config.profile.coverPick')}
          </Button>
          {cover ? (
            <Button variant="link" className="rd-prefs__link" onClick={removeCover}>
              {t('profile.cover.remove')}
            </Button>
          ) : null}
        </div>
        <p className="rd-prefs__desc">{t('config.profile.coverHint')}</p>
      </div>
      {picking ? <ProfileCoverPicker initial={cover} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}

export function ProfileSection() {
  const t = useT();
  const me = useApp((s) => s.user);
  const myStatus = useApp((s) => s.myStatus);
  const myProfile = useApp((s) => s.myProfile);
  const loadMyProfile = useApp((s) => s.loadMyProfile);
  const viewProfile = useApp((s) => s.viewProfile);
  const save = useApp((s) => s.saveProfile);
  const mine = myProfile && myProfile.user.id === me?.id ? myProfile : null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  // carrega o meu perfil (à parte do popup, que pode estar no de outra pessoa)
  const meId = me?.id;
  useEffect(() => {
    if (meId) void loadMyProfile();
  }, [meId, loadMyProfile]);

  if (!me || !mine) return <p className="rd-prefs__desc">{t('config.profile.loading')}</p>;
  const saved: Draft = { bio: mine.bio, rpg: mine.rpgPreferences, times: mine.timePreferences };
  const current = draft ?? saved;
  const dirty = draft !== null && (draft.bio !== saved.bio || draft.rpg !== saved.rpg || draft.times !== saved.times);

  const submit = async () => {
    setSaving(true);
    const ok = await save({ bio: current.bio, rpgPreferences: current.rpg, timePreferences: current.times });
    setSaving(false);
    // não deu: o texto fica no campo para tentar de novo (o erro já aparece no aviso)
    if (!ok) return;
    setDraft(null);
    toast(t('config.profile.saved'), 'success');
  };

  return (
    <>
      <div className="rd-prefs__profile">
        <section className="rd-prefs__card rd-prefs__profileForm" aria-label={t('prefs.profile.title')}>
          <AvatarRow />
          <CoverRow cover={mine.cover} />
          {FIELDS.map((field) => (
            <div key={field.key} className="rd-prefs__field" data-pref-row={field.key}>
              <label htmlFor={`pref-${field.key}`}>{t(field.label)}</label>
              <textarea
                id={`pref-${field.key}`}
                className="rd-prefs__textarea"
                rows={field.rows}
                maxLength={field.max}
                placeholder={t(field.placeholder)}
                value={current[field.key]}
                onChange={(e) => setDraft({ ...current, [field.key]: e.target.value })}
              />
            </div>
          ))}
        </section>
        <aside className="rd-prefs__profilePreview" aria-label={t('prefs.profile.preview')}>
          <h3 className="rd-prefs__eyebrow">{t('prefs.profile.preview')}</h3>
          <div className="rd-prefs__card rd-prefs__previewCard">
            <div className="rd-prefs__previewWho">
              <Avatar name={me.displayName} color={me.color} size={48} userId={me.id} avatarVersion={me.avatarVersion} presence={myStatus ?? undefined} />
              <strong>{me.displayName}</strong>
            </div>
            {FIELDS.map((field) => (
              <div key={field.key}>
                <div className="rd-prefs__previewLabel">{t(field.label)}</div>
                <div className={`rd-prefs__previewText${current[field.key] ? '' : ' is-empty'}`}>{current[field.key] || t('prefs.profile.empty')}</div>
              </div>
            ))}
          </div>
          <Button variant="link" className="rd-prefs__link" onClick={() => void viewProfile(me.id)}>
            {t('prefs.profile.open')}
          </Button>
        </aside>
      </div>
      {dirty ? (
        <div className="rd-prefs__unsaved" role="region" aria-label={t('prefs.profile.unsaved')}>
          <span>{t('prefs.profile.unsaved')}</span>
          <Button variant="secondary" onClick={() => setDraft(null)}>
            {t('prefs.profile.discard')}
          </Button>
          <Button variant="action" busy={saving} onClick={() => void submit()}>
            {t('prefs.profile.save')}
          </Button>
        </div>
      ) : null}
    </>
  );
}
